import { TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '../../ts/models/resourceInfoParts';
import { DataSourcesResource } from '../../content/resources/Datasources';
import { ConnectedBreadcrumbs } from '../content/breadcrumbs/ConnectedBreadcrumbs';
import { ServerUrls } from '../ServerUrls';
import { SearchField } from '../../search/components/SearchField';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { AvailableActions } from '../state/AvailableActionsForEntityState';
import { LayoutType, WidthToLayoutType } from '../state/LayoutType';
import { SpaceInfo } from '../state/SpaceInfo';
import { BackButton } from './BackButton';
import { ConnectedPageHeaderMenus } from './ConnectedPageHeaderMenus';
import { PageHeaderNavPanelButton } from './PageHeaderNavPanelButton';
import { PageHeaderSearch } from './PageHeaderSearch';
import { PageHeaderStyles } from './PageHeaderStyles';
import { VizViewerPageHeader } from './VizViewerPageHeader';

export const MAX_RESIZE_EVENTS_PER_SECOND = 5; // Need to adjust number via more testing
export const RESIZE_THROTTLE_INTERVAL = 1000 / MAX_RESIZE_EVENTS_PER_SECOND;
export const pageHeaderSearchActiveStyle = 'tb-page-header-search';

export type PageHeaderButtonType = 'light' | 'regular';

export interface PageHeaderIconPaths {
  readonly active: string;
  readonly inactive: string;
}
export interface PageHeaderIcons {
  readonly lightIcons: PageHeaderIconPaths;
  readonly regularIcons: PageHeaderIconPaths;
}

export interface PageHeaderStateProps {
  readonly isInVizViewerArea: boolean;
  readonly isOnSiteStatusViz: boolean;
  readonly isOnServerStatusViz: boolean;
  readonly inSearchNavLocation: boolean;
  readonly isInServerArea: boolean;
  readonly isMetricDetail: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly siteUrls: SpacesSiteUrls;
  readonly previousUrl: string;
  readonly workbookActions: AvailableActions;
  readonly displayBreadCrumbs: boolean;
  readonly layoutType: LayoutType;
  readonly searchTerm: string;
}

export interface PageHeaderDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly getWorkbookActions: (workbook: IExplorableItemTypeInfo) => void;
  readonly setLayoutType: (layoutType: LayoutType) => void;
}

export interface PageHeaderOwnProps extends ReactDirectiveProps {
  readonly datasourcesResource: DataSourcesResource;
}

export interface PageHeaderState {
  readonly pageHeaderSearchStyle: string;
}

export type PageHeaderProps = PageHeaderStateProps & PageHeaderDispatchProps & PageHeaderOwnProps;

export class PageHeader extends React.PureComponent<PageHeaderProps> {
  public render(): JSX.Element {
    if (this.props.isInVizViewerArea) { // includes metric detail page
      return <VizViewerPageHeader {...this.props} />;

    } else if (this.props.inSearchNavLocation) {
      return <SearchPageHeader {...this.props} />;

    } else {
      return <NonVizPageHeader {...this.props} />;
    }
  }

  private onWindowResize = () => {
    const windowForDeviceWidth = WidthToLayoutType(window.innerWidth);
    this.props.setLayoutType(windowForDeviceWidth);
  }
  private throttledOnWindowResize = _.throttle(this.onWindowResize, RESIZE_THROTTLE_INTERVAL);

  public componentDidMount() {
    window.addEventListener('resize', this.throttledOnWindowResize);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttledOnWindowResize);
  }
}

export class NonVizPageHeader extends React.Component<PageHeaderProps, PageHeaderState> {
  public constructor(props: PageHeaderProps) {
    super(props);

    this.state = {pageHeaderSearchStyle: ''};
  }

  public render() {
    const showNavPanelButton = this.props.layoutType === 'mobile' || this.props.layoutType === 'tablet';
    return (
      <div className={PageHeaderStyles.Container}>
        <Region>
          {showNavPanelButton && <LeftItem><PageHeaderNavPanelButton serverUrls={ServerUrls.getInstance()} addPadding={true}/></LeftItem>}
          {this.props.displayBreadCrumbs && <LeftItem><ConnectedBreadcrumbs /></LeftItem>}
        </Region>

        <Region>
          {!this.props.isInServerArea &&
            <RightItem>
              {PageHeaderSearch({headerSearchStyle: this.state.pageHeaderSearchStyle, angularContext: this.props.angularContext, searchClosed: this.onSearchClosed, searchClosedWithoutNeedingFocus: this.onSearchClosed, isSearchInputBoxActive: this.isSearchInputBoxActive})}
            </RightItem>
          }
          <ConnectedPageHeaderMenus buttonType="regular" showFavoritesMenu={false} />
        </Region>
      </div>
    );
  }

  private isSearchInputBoxActive = (isActive: boolean) => {
    this.setState({pageHeaderSearchStyle: isActive ? pageHeaderSearchActiveStyle : ''});
  };

  private onSearchClosed = () => {
    // Angular doesn't want to update when the NLP iframe has focus
    // So we force a render of the parent react component to get the
    // Omnibox to update.
    this.forceUpdate();
  }
}

export class SearchPageHeader extends React.Component<PageHeaderProps> {
  public render() {
    const showNavPanelButton = this.props.layoutType === 'mobile' || this.props.layoutType === 'tablet';
    return (
      <div className={PageHeaderStyles.Container}>
        {showNavPanelButton && <Region><LeftItem><PageHeaderNavPanelButton serverUrls={ServerUrls.getInstance()} addPadding={false} /></LeftItem></Region>}
          <BackButton
            previousUrl={this.props.previousUrl || this.props.siteUrls.defaultUrl()} // default to user's start page
            parentId={''}
            siteUrls={this.props.siteUrls}
            serverUrls={ServerUrls.getInstance()}
            isOnDatasourceAnalyticsViz={false}
            isOnSiteStatusViz={false}
            isOnServerStatusViz={false}
            isMetricDetail={false}
            buttonType={'regular'}
            styleOverrides={{
              marginLeft: TabStyles.Spacing.ExtraLargeSpace,
              marginRight: TabStyles.Spacing.ExtraLargeSpace,
              height: '26px'}}
            wrapperDivStyleOverrides={{ marginTop: '0px'}}
          />
          <SearchField searchTerm={this.props.searchTerm} />
        <Region styleOverrides={{paddingRight:'10px'}}>
          <ConnectedPageHeaderMenus buttonType="regular" showFavoritesMenu={false} />
        </Region>
      </div>
    );
  }
}

export function Region(props: { children: React.ReactNode, styleOverrides?: React.CSSProperties }) {
  return (
    <div className={PageHeaderStyles.Region} style={{...props.styleOverrides}}>
      {props.children}
    </div>
  );
}

export function LeftItem(props: { children: React.ReactNode, className?: string }) {
  return (
    <div className={PageHeaderStyles.LeftItem + ' ' + props.className}>
      {props.children}
    </div>
  );
}

export function RightItem(props: { children: React.ReactNode, className?: string }) {
  return (
    <div className={PageHeaderStyles.RightItem + ' ' + props.className}>
      {props.children}
    </div>
  );
}
